import { useTranslation } from '@mukil/i18n';
import { Button, ConfirmDialog, StatusPill } from '@jumpstone/ui-react';
import { format_date, format_number } from '../../lib/format';
import { LEAVE_STATUS_KEY, LEAVE_STATUS_PILL } from '../../lib/label_keys';
import type {
  LeaveActionFailure,
  LeaveCancelFailure,
} from '../../data/use_leave_requests';
import type { LeaveRequest } from '../../data/wagepro_store_leave';
import {
  is_actionable,
  is_cancellable,
  leave_action_error_message,
  leave_cancel_error_message,
} from './leave_utils';
import { useState } from 'react';

/** One leave-request queue row: the request's data plus its own approve/reject affordances and any action failure. */
export function LeaveTableRow({
  request,
  locale,
  can_act,
  is_approving,
  action_failure,
  cancel_failure,
  on_approve_click,
  on_reject_click,
  on_cancel,
}: {
  request: LeaveRequest;
  locale: string;
  /** `true` only when the caller holds a write-capable OAuth grant — see `use_leave_requests.ts`. */
  can_act: boolean;
  is_approving: boolean;
  action_failure: LeaveActionFailure | undefined;
  cancel_failure: LeaveCancelFailure | undefined;
  on_approve_click: () => void;
  on_reject_click: () => void;
  on_cancel: (() => Promise<void>) | undefined;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [cancel_dialog_open, set_cancel_dialog_open] = useState(false);
  const [is_cancelling, set_is_cancelling] = useState(false);

  return (
    <tr>
      <td className="font-semibold">
        {request.employee_name ?? t('leave.unknown_employee')}
      </td>
      <td>
        {t(`leave_type.${request.leave_type}`, {
          defaultValue: request.leave_type,
        })}
      </td>
      <td>
        {t('leave.date_range', {
          start: format_date(request.start_date, locale),
          end: format_date(request.end_date, locale),
        })}
      </td>
      <td className="text-end num">
        {format_number(request.days_requested, locale)}
      </td>
      <td>
        <StatusPill
          status={LEAVE_STATUS_PILL[request.status]}
          label={t(LEAVE_STATUS_KEY[request.status])}
        />
      </td>
      <td className="text-end whitespace-nowrap">
        {is_actionable(request) && can_act && (
          <>
            <Button
              variant="ghost"
              size="xs"
              loading={is_approving}
              onClick={on_approve_click}
            >
              {t('common.approve')}
            </Button>
            <Button variant="ghost-danger" size="xs" onClick={on_reject_click}>
              {t('common.reject')}
            </Button>
          </>
        )}
        {is_cancellable(request) && can_act && on_cancel !== undefined && (
          <>
            <Button
              variant="ghost-danger"
              size="xs"
              onClick={() => set_cancel_dialog_open(true)}
            >
              {t('leave.cancel_action')}
            </Button>
            <ConfirmDialog
              open={cancel_dialog_open}
              onOpenChange={set_cancel_dialog_open}
              title={t('leave.cancel_confirm_title')}
              description={t('leave.cancel_confirm_description')}
              confirmLabel={t('leave.cancel_confirm_button')}
              cancelLabel={t('common.cancel')}
              variant="destructive"
              loading={is_cancelling}
              onConfirm={() => {
                set_is_cancelling(true);
                void on_cancel().then(() => {
                  set_is_cancelling(false);
                  set_cancel_dialog_open(false);
                });
              }}
            />
          </>
        )}
        {action_failure !== undefined && (
          <p className="nv-help is-error mt-1" role="alert">
            {leave_action_error_message(t, action_failure)}
          </p>
        )}
        {cancel_failure !== undefined && (
          <p className="nv-help is-error mt-1" role="alert">
            {leave_cancel_error_message(t, cancel_failure)}
          </p>
        )}
      </td>
    </tr>
  );
}
